// @ts-nocheck
import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Form, Card, Badge, Alert } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { Tooltip } from 'react-tooltip';
import { useGetLedgerAccounts } from '../../../services/loanService';
import { LedgerAccount, AccountStatus } from '../../../services/schemas/loanServiceSchemas';
import { formatDate } from '../../../utils/formatters';

// Add custom styles
const customStyles = {
  dataTable: {
    rows: {
      style: {
        minHeight: '45px',
      }
    },
    headCells: {
      style: {
        fontWeight: 600,
        paddingLeft: '8px',
        paddingRight: '8px',
        fontSize: '14px',
      }
    },
    cells: {
      style: {
        paddingLeft: '8px',
        paddingRight: '8px',
      }
    }
  }
};

const LoanAccounts: React.FC = () => {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);
    const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);

    const [formValues, setFormValues] = useState({
        accountNumber: '',
        customerNumber: '',
        status: undefined as AccountStatus | undefined,
        startDateFrom: '',
        startDateTo: '',
        page: 0,
        size: 10
    });
    // Initial load using getLedgerAccounts
    const { data: accountsData, isLoading, error } = useGetLedgerAccounts({
        page: page < 0 ? 0 : page - 1,
        size,
        ...(formValues.status ? { status: formValues.status } : {})
    });

    // The whole book, for the summary cards. The table below is paged and filtered; the cards are
    // not. The book is small (a few hundred accounts), so one call gives exact totals.
    const { data: bookData, isLoading: isBookLoading } = useGetLedgerAccounts({ page: 0, size: 1000 });
    const summary = useMemo(() => {
        const all: LedgerAccount[] = bookData?.content ?? [];
        const inArrears = all.filter(a => a.status === 'IN_ARREARS' || a.status === 'DEFAULTED');
        const sum = (list: LedgerAccount[], field: 'totalOutstanding' | 'disbursedAmount') =>
            list.reduce((total, a) => total + (a[field] || 0), 0);
        return {
            total: bookData?.totalElements ?? all.length,
            active: all.filter(a => a.status === 'ACTIVE').length,
            inArrears: inArrears.length,
            arrearsOutstanding: sum(inArrears, 'totalOutstanding'),
            outstanding: sum(all.filter(a => a.status !== 'CLOSED' && a.status !== 'WRITTEN_OFF' && a.status !== 'CANCELLED'), 'totalOutstanding'),
            disbursed: sum(all, 'disbursedAmount'),
            pending: all.filter(a => a.status === 'PENDING').length,
            customers: new Set(all.map(a => a.customerNumber || a.customerName)).size
        };
    }, [bookData]);

    // Get error message
    const errorMessage = (error as any)?.message || 'An error occurred while fetching accounts. Please try again later.';

    const handleSearch = () => {
        // Status is applied in query; reset to page 1 to refresh
        setPage(1);
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handlePerRowsChange = (newPerPage: number) => {
        setSize(newPerPage);
        setPage(1);
    };

    const handleReset = () => {
        // Clear form values
        setFormValues(prev => ({
            accountNumber: '',
            customerNumber: '',
            status: undefined,
            startDateFrom: '',
            startDateTo: '',
            page: 0,
            size: 10
        }));
        // Reset to page 1
        setPage(1);
    };

    const handleCreateAccount = () => {
        navigate('/accounts/create-direct');
    };

    const getBadgeVariant = (status: AccountStatus) => {
        switch (status) {
            case 'ACTIVE':
                return 'success';
            case 'IN_ARREARS':
                return 'warning';
            case 'DEFAULTED':
                return 'danger';
            case 'PAID_OFF':
                return 'info';
            case 'WRITTEN_OFF':
                return 'dark';
            case 'CLOSED':
                return 'secondary';
            case 'PENDING':
                return 'primary';
            default:
                return 'secondary';
        }
    };

    // Format currency
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount);
    };

    // Group accounts by customer
    const groupedAccounts = useMemo(() => {
        if (!accountsData?.content) return [];

        const grouped = accountsData.content.reduce((acc: { [key: string]: LedgerAccount[] }, account: LedgerAccount) => {
            const customerKey = account.customerId || account.customerName || 'Unknown';
            if (!acc[customerKey]) {
                acc[customerKey] = [];
            }
            acc[customerKey].push(account);
            return acc;
        }, {});

        return (Object.entries(grouped) as [string, LedgerAccount[]][]).map(([customerKey, accounts]) => ({
            customerKey,
            customerName: accounts[0].customerName || 'Unknown Customer',
            customerNumber: accounts[0].customerNumber || 'Unknown',
            accounts,
            totalAccounts: accounts.length,
            dueAccounts: accounts.filter(acc => acc.status === 'IN_ARREARS' || acc.status === 'DEFAULTED').length,
            totalOutstanding: accounts.reduce((sum, acc) => sum + (acc.totalOutstanding || 0), 0),
            totalLoanAmount: accounts.reduce((sum: number, acc: LedgerAccount) => sum + (acc.disbursedAmount || 0), 0),
        }));
    }, [accountsData?.content]);

    const customerColumns = [
        {
            name: 'Customer Name',
            selector: (row: any) => row.customerName,
            sortable: true,
            grow: 2
        },
        {
            name: 'Customer Number',
            selector: (row: any) => row.customerNumber,
            sortable: true,
            grow: 1
        },
        {
            name: 'Total Accounts',
            selector: (row: any) => row.totalAccounts,
            sortable: true,
            grow: 1,
            cell: (row: any) => (
                <div className="d-flex align-items-center">
                    <div className="avatar-sm me-2">
                        <div className="avatar-title bg-soft-success text-success rounded-circle">
                            <i className="fa fa-file-invoice"></i>
                        </div>
                    </div>
                    <div>
                        <span className="fw-bold">{row.totalAccounts}</span>
                    </div>
                </div>
            )
        },
        {
            name: 'Due Accounts',
            selector: (row: any) => row.dueAccounts,
            sortable: true,
            grow: 1,
            cell: (row: any) => (
                <div className="d-flex align-items-center">
                    <div className="avatar-sm me-2">
                        <div className="avatar-title bg-soft-danger text-danger rounded-circle">
                            <i className="fa fa-exclamation-circle"></i>
                        </div>
                    </div>
                    <div>
                        <span className="fw-bold">{row.dueAccounts}</span>
                    </div>
                </div>
            )
        },
        {
            name: 'Total Outstanding',
            selector: (row: any) => row.totalOutstanding,
            sortable: true,
            grow: 1,
            cell: (row: any) => formatCurrency(row.totalOutstanding)
        },
        {
            name: 'Total Loan Amount',
            selector: (row: any) => row.totalLoanAmount,
            sortable: true,
            grow: 1,
            cell: (row: any) => formatCurrency(row.totalLoanAmount)
        },
        {
            name: 'Actions',
            cell: (row: any) => (
                <div className="d-flex gap-2">
                    <Link to={`/customers/${row.customerNumber}/accounts`}>
                        <button
                            className="btn btn-sm btn-primary"
                            data-tooltip-id={`view-accounts-${row.customerNumber}`}
                        >
                            <i className="fa fa-list"></i>
                        </button>
                    </Link>
                    <Tooltip id={`view-accounts-${row.customerNumber}`} place="top" content="View Customer Accounts" />
                </div>
            ),
            grow: 0.5,
            center: true
        }
    ];

    const columns = [
        {
            name: 'Account Number',
            selector: (row: LedgerAccount) => row.accountNumber || '',
            sortable: true,
            cell: (row: LedgerAccount) => (
                <Link to={`/loan-accounts/${row.accountId}`} className="fw-bold text-primary">
                    {row.accountNumber || row.accountId.substring(0, 8)}
                </Link>
            ),
            width: '160px'
        },
        {
            name: 'Customer Name',
            selector: (row: LedgerAccount) => row.customerName || '',
            sortable: true,
            width: '150px'
        },
        {
            name: 'Loan Amount',
            selector: (row: LedgerAccount) => row.disbursedAmount || 0,
            sortable: true,
            cell: (row: LedgerAccount) => formatCurrency(row.disbursedAmount || 0),
            width: '150px'
        },
        {
            name: 'Outstanding',
            selector: () => 0,
            sortable: false,
            cell: () => (
                <span className="text-muted">N/A</span>
            ),
            width: '150px'
        },
        {
            name: 'Status',
            selector: (row: LedgerAccount) => row.status || '',
            sortable: true,
            width: '120px',
            cell: (row: LedgerAccount) => (
                <Badge bg={getBadgeVariant(row.status)}>{row.status}</Badge>
            ),
        },
        {
            name: 'Disbursement Date',
            selector: (row: LedgerAccount) => row.disbursementDate || '',
            sortable: true,
            cell: (row: LedgerAccount) => formatDate(row.disbursementDate),
            width: '150px'
        },
        {
            name: 'Maturity Date',
            selector: (row: LedgerAccount) => row.maturityDate || '',
            sortable: true,
            cell: (row: LedgerAccount) => formatDate(row.maturityDate),
            width: '150px'
        },
        {
            name: 'First Due Date',
            selector: (row: LedgerAccount) => row.firstDueDate || '',
            sortable: true,
            cell: (row: LedgerAccount) => formatDate(row.firstDueDate),
            width: '150px'
        },
        {
            name: 'Actions',
            cell: (row: LedgerAccount) => (
                <div className="d-flex gap-2">
                    <Link to={`/loan-accounts/${row.accountId}`}>
                        <Button variant="primary" size="sm">
                            <i className="fa fa-eye me-1"></i>
                            View
                        </Button>
                    </Link>
                </div>
            ),
            width: '100px',
            center: true
        }
    ];

    const formatCurrencyShort = (amount: number) =>
        new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 }).format(amount);

    const summaryCards = [
        {
            label: 'Loan Accounts',
            value: summary.total,
            detail: `${summary.active} active · ${summary.customers} customer${summary.customers === 1 ? '' : 's'}`,
            icon: 'fa-folder-open', color: 'primary', border: 'rgba(0,123,255,0.2)'
        },
        {
            label: 'In Arrears',
            value: summary.inArrears,
            detail: `${formatCurrencyShort(summary.arrearsOutstanding)} outstanding`,
            icon: 'fa-exclamation-triangle', color: 'danger', border: 'rgba(220,53,69,0.2)'
        },
        {
            label: 'Total Outstanding',
            value: formatCurrencyShort(summary.outstanding),
            detail: `${formatCurrencyShort(summary.disbursed)} disbursed`,
            icon: 'fa-money', color: 'success', border: 'rgba(40,167,69,0.2)'
        },
        {
            label: 'Pending Disbursement',
            value: summary.pending,
            detail: 'Approved, not yet paid out',
            icon: 'fa-hourglass-half', color: 'warning', border: 'rgba(255,193,7,0.3)'
        }
    ];

    return (
        <>
            {/* Header - the same green banner as Leads */}
            <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-4 bg-primary-gradient">
                <div className="container-fluid">
                    <div className="row g-3 mb-3 align-items-center">
                        <div className="col">
                            <ol className="breadcrumb bg-transparent mb-0">
                                <li className="breadcrumb-item">
                                    <Link className="text-white" to="/"><i className="fa fa-home me-2"></i>Home</Link>
                                </li>
                                <li className="breadcrumb-item active text-white" aria-current="page">Loan Accounts</li>
                            </ol>
                        </div>
                    </div>
                    <div className="row align-items-center">
                        <div className="col-md-6">
                            <h1 className="fs-2 fw-bold mb-2 text-white">Loan Accounts</h1>
                            <p className="mb-0 text-white fs-6 opacity-75">
                                Every disbursed loan, grouped by customer, with what is still owed
                            </p>
                        </div>
                        <div className="col-md-6 text-md-end">
                            <div className="d-flex gap-3 justify-content-md-end flex-wrap align-items-center">
                                <Button
                                    variant="light"
                                    className="shadow-lg px-4 py-2 rounded-pill fw-semibold"
                                    style={{ backgroundColor: '#ffffff', color: '#495057', border: '2px solid rgba(255,255,255,0.8)', fontSize: '0.9rem', minWidth: '160px' }}
                                    onClick={() => setShowAdvancedSearch(s => !s)}
                                >
                                    <i className={`fa ${showAdvancedSearch ? 'fa-times' : 'fa-search'} me-2`} style={{ fontSize: '0.85rem' }}></i>
                                    {showAdvancedSearch ? 'Hide Search' : 'Advanced Search'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
            <div className="container-fluid">
                {/* Summary Cards - the whole book, whatever the table below is filtered to */}
                <div className="row g-4 mb-4">
                    {summaryCards.map(card => (
                        <div key={card.label} className="col-lg-3 col-md-6">
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Body className="p-4">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <p className="text-muted small mb-1">{card.label}</p>
                                            <h3 className={`mb-1 fw-bold text-${card.color}`}>
                                                {isBookLoading
                                                    ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                    : card.value}
                                            </h3>
                                            {!isBookLoading && <small className="text-muted">{card.detail}</small>}
                                        </div>
                                        <div className="rounded-circle p-3" style={{ backgroundColor: '#f8f9fa', border: `1px solid ${card.border}` }}>
                                            <i className={`fa ${card.icon} fa-2x text-${card.color}`} style={{ display: 'block' }}></i>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    ))}
                </div>

                <div className="row g-3 mb-3 row-deck">
                    <div className="col-12">
                        <div className="card border-0 shadow-sm">
                            <div className="card-header bg-white d-flex justify-content-between align-items-center">
                                <h6 className="card-title mb-0">
                                    <i className="fa fa-users text-primary me-2"></i>
                                    Accounts by customer
                                </h6>
                            </div>
                            <div className="card-body">
                                {error && (
                                    <Alert variant="danger" className="mb-4">
                                        <div className="d-flex align-items-center">
                                            <i className="fa fa-exclamation-circle me-2"></i>
                                            <div>
                                                <h6 className="mb-1">Error Loading Accounts</h6>
                                                <p className="mb-0">{errorMessage}</p>
                                            </div>
                                        </div>
                                    </Alert>
                                )}

                                {showAdvancedSearch && (
                                    <Card className="mb-3">
                                        <Card.Body>
                                            <Form>
                                                <div className="row g-3">
                                                    <div className="col-md-4">
                                                        <Form.Group>
                                                            <Form.Label>Account Number</Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                placeholder="Enter account number"
                                                                value={formValues.accountNumber}
                                                                onChange={(e) => setFormValues(prev => ({ ...prev, accountNumber: e.target.value }))}
                                                                disabled
                                                                title="Account number filtering not available with new ledger API"
                                                            />
                                                        </Form.Group>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <Form.Group>
                                                            <Form.Label>Customer Number</Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                placeholder="Enter customer number"
                                                                value={formValues.customerNumber}
                                                                onChange={(e) => setFormValues(prev => ({ ...prev, customerNumber: e.target.value }))}
                                                                disabled
                                                                title="Customer number filtering not available with new ledger API"
                                                            />
                                                        </Form.Group>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <Form.Group>
                                                            <Form.Label>Status</Form.Label>
                                                            <Form.Select
                                                                value={formValues.status || ''}
                                                                onChange={(e) => setFormValues(prev => ({ ...prev, status: e.target.value as AccountStatus }))}
                                                            >
                                                                <option value="">All</option>
                                                                <option value="PENDING">Pending</option>
                                                                <option value="ACTIVE">Active</option>
                                                                <option value="IN_ARREARS">In Arrears</option>
                                                                <option value="DEFAULTED">Defaulted</option>
                                                                <option value="WRITTEN_OFF">Written Off</option>
                                                                <option value="CLOSED">Closed</option>
                                                            </Form.Select>
                                                        </Form.Group>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <Form.Group>
                                                            <Form.Label>Start Date From</Form.Label>
                                                            <Form.Control
                                                                type="date"
                                                                value={formValues.startDateFrom}
                                                                onChange={(e) => setFormValues(prev => ({ ...prev, startDateFrom: e.target.value }))}
                                                                disabled
                                                                title="Date filtering not available with new ledger API"
                                                            />
                                                        </Form.Group>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <Form.Group>
                                                            <Form.Label>Start Date To</Form.Label>
                                                            <Form.Control
                                                                type="date"
                                                                value={formValues.startDateTo}
                                                                onChange={(e) => setFormValues(prev => ({ ...prev, startDateTo: e.target.value }))}
                                                                disabled
                                                                title="Date filtering not available with new ledger API"
                                                            />
                                                        </Form.Group>
                                                    </div>
                                                    <div className="col-12">
                                                        <div className="d-flex justify-content-end gap-2">
                                                            <Button variant="secondary" onClick={handleReset}>
                                                                <i className="fa fa-undo me-2"></i>
                                                                Reset
                                                            </Button>
                                                            <Button variant="primary" onClick={handleSearch}>
                                                                <i className="fa fa-search me-2"></i>
                                                                Search
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Form>
                                        </Card.Body>
                                    </Card>
                                )}

                                {/* Customer Summary Table */}
                                <div className="table-responsive w-100">
                                    <DataTable
                                        columns={customerColumns}
                                        data={groupedAccounts}
                                        pagination
                                        paginationServer
                                        paginationTotalRows={accountsData?.totalElements || 0}
                                        paginationDefaultPage={page}
                                        onChangePage={handlePageChange}
                                        onChangeRowsPerPage={handlePerRowsChange}
                                        highlightOnHover
                                        striped
                                        responsive
                                        paginationPerPage={size}
                                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                        paginationComponentOptions={{
                                            rowsPerPageText: 'Rows per page:',
                                            rangeSeparatorText: 'of',
                                        }}
                                        progressPending={isLoading}
                                        progressComponent={
                                            <div className="text-center py-3">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </div>
                                        }
                                        noDataComponent={
                                            <div className="p-4 text-center">
                                                <p className="mb-0">No customers found</p>
                                            </div>
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default LoanAccounts;
